import React, { useState, useEffect } from "react";
import { CloseIcon } from "./Icons";
import { useMindspaceStore } from "@store";
import { trackEvent } from "../../services/analyticsService";
import { logDebug, logError } from "../../utils/logging";
import styles from "./BusinessProfileModal.module.css";
import { useIsGuest } from "@store";
import { fetchContextKeyDefinitions, ContextKeyDefinition } from "../../services/apiClientV2";

interface BusinessProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Dynamic form data
type FormData = Record<string, string>;

interface FormErrors {
  general?: string;
  [key: string]: string | undefined;
}

const CATEGORY_LABELS: Record<string, string> = {
  identity: "Identitate Brand",
  strategic: "Obiective Strategice",
  context: "Context Piață",
  preferences: "Preferințe",
  altele: "Altele"
};

const BusinessProfileModal: React.FC<BusinessProfileModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {

    activeMindspaceId,
    updateContextItem,
    getContextValue,
    error: mindspaceError,
    clearError,
  } = useMindspaceStore();



  const [formData, setFormData] = useState<FormData>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [keyDefinitions, setKeyDefinitions] = useState<ContextKeyDefinition[]>([]);
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);

  const isGuest = useIsGuest();

  // Load keys and data
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      if (isOpen && activeMindspaceId) {
        setIsLoadingKeys(true);
        try {
          // Fetch definitions
          const defs = await fetchContextKeyDefinitions();

          if (mounted) {
            // Filter persistent keys only
            const persistentDefs = defs.filter(d => d.is_persistent && d.is_active);

            // Sort by order
            persistentDefs.sort((a, b) => a.sort_order - b.sort_order);

            setKeyDefinitions(persistentDefs);

            // Populate form data
            const newFormData: FormData = {};
            persistentDefs.forEach(def => {
              newFormData[def.key_name] = getContextValue(def.key_name) || "";
            });
            setFormData(newFormData);

            setErrors({});
            clearError();

            trackEvent("business_profile_modal_opened", {
              mindspace_id: activeMindspaceId,
              keys_count: persistentDefs.length,
            });
          }
        } catch (err) {
          logError("Failed to load context definitions", err);
          if (mounted) setErrors({ general: "Nu am putut încărca definițiile contextului." });
        } finally {
          if (mounted) setIsLoadingKeys(false);
        }
      }
    };
    init();
    return () => { mounted = false; };
  }, [isOpen, activeMindspaceId, getContextValue, clearError]);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Generic validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    // Currently no strict validation required by DB, but we can check if empty? 
    // Actually, fields can be optional. 
    // If strict validation is needed, we would add is_required to ContextKeyDefinition. 
    // For now, allow empty but maybe warn? 
    // Existing code required length > 10 for descriptions. 
    // I'll keep it simple: Validate if non-empty but too short?
    // Let's rely on user discretion for now to be strictly dynamic.

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !activeMindspaceId) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      logDebug("[BusinessProfileModal] Submitting form data:", formData);

      const updatePromises = Object.entries(formData).map(([key, value]) => {
        // Compare with existing context to avoid unnecessary calls? 
        // updateContextItem implementation handles upsert.
        // We only send if value is different from existing context?
        // Optimization:
        const current = getContextValue(key);
        if (value.trim() !== current.trim()) {
          return updateContextItem(key, value.trim());
        }
        return Promise.resolve();
      });

      await Promise.all(updatePromises);

      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 3000);

      trackEvent("business_profile_updated", {
        mindspace_id: activeMindspaceId,
      });

      logDebug("[BusinessProfileModal] Profile updated successfully");

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      logError("[BusinessProfileModal] Error updating profile:", error);
      setErrors({ general: "A apărut o eroare la salvarea datelor" });
      trackEvent("business_profile_update_failed", {
        mindspace_id: activeMindspaceId,
        error: String(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Group keys
  const groupedKeys = React.useMemo(() => {
    const groups: Record<string, ContextKeyDefinition[]> = {};
    keyDefinitions.forEach(def => {
      const cat = def.category || "altele";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(def);
    });
    return groups;
  }, [keyDefinitions]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div
        className={styles.modal}
        role="dialog"
        aria-labelledby="business-profile-title"
        aria-modal="true"
      >
        <div className={styles.header}>
          <h2 id="business-profile-title" className={styles.title}>
            Profil afacere
          </h2>
          <button
            onClick={handleClose}
            className={styles.closeButton}
            disabled={isSubmitting}
            title="Închide"
            aria-label="Închide modal"
          >
            <CloseIcon className={styles.closeIcon} />
          </button>
        </div>

        <div className={styles.content}>
          {isLoadingKeys ? (
            <div className={styles.loadingState}>Se încarcă câmpurile...</div>
          ) : keyDefinitions.length === 0 ? (
            <div className={styles.emptyState}>Nu există definiții pentru profil.</div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.form}>
              {Object.entries(groupedKeys).map(([category, defs]) => (
                <div key={category} className={styles.categorySection}>
                  <h3 className={styles.categoryHeader}>{CATEGORY_LABELS[category] || category}</h3>
                  {defs.map((def) => (
                    <div key={def.key_name} className={styles.fieldGroup}>
                      <label htmlFor={def.key_name} className={styles.label}>
                        {def.display_label}
                      </label>
                      <textarea
                        id={def.key_name}
                        value={formData[def.key_name] || ""}
                        onChange={(e) => handleInputChange(def.key_name, e.target.value)}
                        className={styles.textarea}
                        placeholder={def.description || `Introdu ${def.display_label.toLowerCase()}...`}
                        rows={3}
                        disabled={isSubmitting}
                      />
                    </div>
                  ))}
                </div>
              ))}

              {(errors.general || mindspaceError) && (
                <div className={styles.generalError}>
                  {errors.general || mindspaceError}
                </div>
              )}

              <div className={styles.actions}>
                <button
                  type="button"
                  onClick={handleClose}
                  className={styles.cancelButton}
                  disabled={isSubmitting}
                >
                  Anulează
                </button>
                {!isGuest && (
                  <button
                    type="submit"
                    className={styles.saveButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Se salvează..." : "Salvează"}
                  </button>
                )}
              </div>
            </form>
          )}

          {showSuccessToast && (
            <div className={styles.successToast}>
              Profilul a fost actualizat cu succes!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessProfileModal;
